import React, {useState} from 'react';
import moment from 'moment-timezone';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CalendarWeekHeader from './CalendarWeekHeader';
import CalendarWeekTimeGrid from './CalendarWeekTimeGrid';
import CalendarTimeBlock from './CalendarTimeBlock';
import CalendarSettingsButton from './CalendarSettingsButton';
import useTimezone from '../../hooks/useTimezone';
import { getWeekDates, getWeeklyAppointmentStyle } from './calendarUtils';

export const CELL_WIDTH = 150;
export const HOUR_HEIGHT = 60;
export const START_HOUR = 8;
export const END_HOUR = 20;
export const HOURS_COUNT = END_HOUR - START_HOUR;

const CalendarWeekView = ({
  date,
  sessions = [],
  eventBlocks = [],
  availabilityBlocks = [],
  boxStyles,
  cellWidth = CELL_WIDTH,
  hourHeight = HOUR_HEIGHT,
  startHour = START_HOUR,
  endHour = END_HOUR,
  showWeekends = false,
  useSummaries = false,
  onDateChange = (e, date) => console.log('Change date', date),
  onEditSession = (e, id) => console.log('Edit session', id),
  onClickAvailability = (e, id) => console.log('Click availability', id),
}) => {
  const timezone = useTimezone();
  const hideWeekends = !showWeekends;
  const baseDate = date
    ? (typeof date === 'string' ? new Date(date) : date)
    : new Date();
  const startDate = moment(baseDate).startOf('week');
  const endDate = moment(baseDate).endOf('week');
  const weekDates = getWeekDates(baseDate);
  const isCurrentWeek = moment().isBetween(startDate, endDate, 'day', '[]');
  const totalWidth = cellWidth * ((hideWeekends ? 5 : 7) + 1);
  return (
    <Box
      sx={{...boxStyles, maxWidth: totalWidth}}
    >
      <Box style={{minHeight: hourHeight}}>
        <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between' p={2}>
          <Stack direction='row' spacing={0} alignItems='center' justifyContent='center' >
            <IconButton
              onClick={e => onDateChange(e, moment(date).subtract(1, 'week').toDate())}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={e => onDateChange(e, moment(date).add(1, 'week').toDate())}
            >
              <ChevronRight />
            </IconButton>
            {!isCurrentWeek && 
              <Button 
                variant='text' 
                onClick={e => onDateChange(e, new Date())}
              >
                Today
              </Button>
            }
          </Stack>
          <Stack direction='column' spacing={0.25} alignItems='center'>
            <Typography variant='h3' sx={{fontWeight: 'bold'}}>
              {startDate.format('MMM D')} - {endDate.format('MMM D')}
            </Typography>
            <CalendarSettingsButton />
          </Stack>
        </Stack>
      </Box>
      <Box sx={{ 
        overflowX: 'auto',
        // minWidth: totalWidth,
        width: '100%',
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        <CalendarWeekHeader 
          cellWidth={cellWidth}
          hourHeight={hourHeight}
          weekDates={weekDates} 
          hideWeekends={hideWeekends}
        />
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <CalendarWeekTimeGrid
            cellWidth={cellWidth}
            hourHeight={hourHeight}
            hideWeekends={hideWeekends} 
            startHour={startHour}
            endHour={endHour}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: totalWidth,
              height: '100%',
              // pointerEvents: 'none',
            }}
          >
            {sessions
              .map(s => ({
                session: s, 
                style: s ? getWeeklyAppointmentStyle({
                  session: s, 
                  timezone,
                  weekDates, 
                  hideWeekends, 
                  cellWidth, 
                  hourHeight,
                  startHour,
                  endHour,
                }) : null
              }))
              .filter(({ session, style }) => !!session || !!style)
              .map(({ session, style }, index) => (
                <CalendarTimeBlock
                  timezone={timezone}
                  key={index}
                  block={session}
                  style={style}
                  onClick={e => onEditSession(e, session.id)}
                />
              ))
            }
            {
              eventBlocks.map((block, index) =>
                <CalendarTimeBlock
                  timezone={timezone}
                  key={`event-${index}`}
                  block={block}
                  style={getWeeklyAppointmentStyle({
                    session: block,
                    timezone,
                    weekDates,
                    hideWeekends,
                    cellWidth,
                    hourHeight,
                    startHour,
                    endHour,
                  })}
                />
              )
            }
            {
              availabilityBlocks.map((block, index) =>
                <CalendarTimeBlock
                  timezone={timezone}
                  key={`avail-${index}`}
                  block={block}
                  style={getWeeklyAppointmentStyle({
                    session: block,
                    timezone,
                    weekDates,
                    hideWeekends,
                    cellWidth,
                    hourHeight,
                    startHour,
                    endHour,
                  })}
                  isAvailabilityBlock={true}
                  useSummaries={useSummaries}
                  onClick={(e, _id) => onClickAvailability && onClickAvailability(e, block.id)}
                />
              )
            }
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CalendarWeekView;
